<template>
  <div
    v-if="chartData"
    class="project-card--animation relative flex h-36 w-full flex-col justify-between rounded-2xl p-6"
    :class="{
      'bg-grey-600': !contract.images,
      'cursor-pointer': !carousel,
    }"
    @click="goToDetails('card')"
  >
    <div
      class="absolute left-0 top-0 z-0 h-full w-full rounded-2xl bg-cover"
      :class="{ 'grayscale filter': contract.state === 'settled' }"
      :style="contract.images ? 'background-image: url(\'' + contract.images.path + '\')' : ''"
    >
      <div class="grey-image-filter absolute left-0 top-0 h-full w-full rounded-2xl" />
    </div>
    <div
      class="relative flex items-start"
      :class="
        contract.contract_label !== '' && contract.state !== 'settled'
          ? 'justify-between'
          : 'justify-end'
      "
    >
      <MktTags :label="contract.contract_label" />
      <div
        v-if="contract.contract_gross_value.total_paid !== null"
        class="flex items-start justify-end"
      >
        <p
          v-if="contract.state === 'settled'"
          class="text-right text-sm text-white/80"
          :class="{ 'mt-1 blur-md filter': privacyMode }"
        >
          {{
            '$ ' + dollarFormatting(
              contract.sums.capital_paid +
                contract.sums.interest_gross_paid +
                contract.sums.partial_settlement_paid
            )
          }}
        </p>
        <p v-else class="text-right text-sm">
          <span class="text-white" :class="{ 'blur-md filter': privacyMode }">
            {{
             '$ ' + dollarFormatting(
                contract.sums.capital_paid +
                  contract.sums.interest_gross_paid +
                  contract.sums.partial_settlement_paid
              )
            }}
          </span>
          <span class="white-text-80" :class="{ 'blur-md filter': privacyMode }">
            {{ ' / $ ' + (dollarFormatting(contract.contract_gross_value.total)) }}
          </span>
        </p>
        <div class="ml-2" style="height: 20px; width: 20px">
          <vue3-chart-js
            :id="chartData.id"
            style="height: 20px; width: 20px"
            :type="chartData.type"
            :data="chartData.data"
            :options="chartData.options"
            @before-render="() => {}"
          >
          </vue3-chart-js>
        </div>
      </div>
    </div>
    <div>
      <p
        class="relative mb-1 overflow-hidden text-ellipsis whitespace-nowrap text-left font-bold text-white"
        :class="{ 'cursor-pointer': carousel }"
        @click="goToDetails('name')"
      >
        {{ contract.project_name }}
      </p>
      <div v-if="contract.state === 'settled'" class="relative flex text-sm text-white">
        <img src="@/assets/svg/contracts/tick-white.svg" alt="clock" class="mr-1" />
        <p>{{ $t('contracts.settled') }}</p>
      </div>
      <div v-else-if="contract.payment_in_arrears" class="relative flex text-sm text-orange-sun-4">
        <img src="@/assets/svg/contracts/clock-orange-sun.svg" alt="clock" class="mr-1" />
        <p>
          {{
            $t('contracts.arrears') +
            ' $' +
            dollarFormatting(contract.total_amount_in_arrears) +
            ' ' +
            $t('wording.since') +
            ' ' +
            dateFormatting(
              new Date(contract.upcoming_payments.next_payment.schedule_date),
              '/',
              true
            ).slice(0, 5)
          }}
        </p>
      </div>
      <div v-else class="relative flex text-sm text-white">
        <img src="@/assets/svg/contracts/money-white.svg" alt="money" class="mr-1" />
        <p
          v-if="contract.upcoming_payments.days_to_next_payment === 0"
          :class="{ 'blur-md filter': privacyMode }"
        >
          {{
            '$ ' +
            dollarFormatting(
              contract.upcoming_payments.next_payment.instalment_investor_net
            ) + ' ' +
            $t('wording.today')
          }}
        </p>
        <p v-else :class="{ 'blur-md filter': privacyMode }">
          {{
            '$ '+ dollarFormatting(
              contract.upcoming_payments.next_payment.instalment_investor_net
            ) +
            ' ' +
            $t('wording.in') +
            ' ' +
            $t(
              'wording.day',
              { n: contract.upcoming_payments.days_to_next_payment },
              contract.upcoming_payments.days_to_next_payment
            )
          }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import useGlobalHelpers from '@/mixins/useGlobalHelpers.js'
import Vue3ChartJs from '@j-t-mcc/vue3-chartjs'
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import MktTags from '@/components/contracts/MktTags.vue'
export default {
  components: {
    Vue3ChartJs,
    MktTags
  },
  props: {
    contract: {
      type: Object,
      default: () => {}
    },
    carousel: {
      type: Boolean,
      default: false
    },
    privacyMode: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const { dollarFormatting, slugIt, dateFormatting } = useGlobalHelpers()
    const router = useRouter()

    const totalPaid = computed(() => {
      return (
        props.contract.sums.capital_paid +
        props.contract.sums.interest_gross_paid +
        props.contract.sums.partial_settlement_paid
      )
    })

    const chartData = computed(() => {
      return {
        id: 'doughnut',
        type: 'doughnut',
        data: {
          datasets: [
            {
              data:
                props.contract.state === 'settled'
                  ? [totalPaid.value]
                  : props.contract.payment_in_arrears
                    ? [
                        totalPaid.value,
                        props.contract.total_amount_in_arrears,
                        props.contract.contract_gross_value.total -
                          (totalPaid.value + props.contract.total_amount_in_arrears),
                      ]
                    : [
                        totalPaid.value,
                        props.contract.contract_gross_value.total - totalPaid.value,
                      ],
              backgroundColor: props.contract.payment_in_arrears
                ? ['#65dd98', '#ffb700', '#ffffff80']
                : ['#65dd98', '#ffffff80'],
              borderWidth: 0
            }
          ]
        },
        options: {
          responsive: false,
          maintainAspectRatio: false,
          cutout: '75%',
          plugins: {
            legend: {
              display: false
            },
            tooltip: {
              enabled: false
            }
          }
        }
      }
    })

    const goToDetails = (origin) => {
      if ((origin === 'card' && !props.carousel) || (origin === 'name' && props.carousel))
        if (props.contract) {
          const slug = props.contract.project_name
            ? slugIt(props.contract.project_name) + '-' + props.contract.id
            : props.contract.id
          router.push('/contract-details/' + slug)
        }
    }

    return {
      dollarFormatting,
      dateFormatting,
      chartData,
      goToDetails
    }
  }
}
</script>

<style scoped>
.white-text-80 {
  color: rgba(255, 255, 255, 0.8);
}
</style>
